import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import { Button, Container, FormControl, Grid, InputLabel, Link, MenuItem, Paper, Select, Typography, SelectChangeEvent } from '@mui/material';
import { getWorkflows } from '@/redux/slices/profileSlice';
import { selectWorkflows } from '@/redux/selectors/profileSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

const SchemaLab = () => {
  const dispatch = useAppDispatch();
  const workflows = useAppSelector(selectWorkflows);
  const [schemaInput, setSchemaInput] = useState<string>('');
  const [parsedSchema, setParsedSchema] = useState<any>();
  const [errors, setErrors] = useState<string>('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');
  const [selectedWorkflowVariant, setSelectedWorkflowVariant] = useState<string>('');
  const [formValues, setFormValues] = useState<any>({});
  const schemaRef = React.useRef<any>(null);

  useEffect(() => {
    dispatch(getWorkflows());
  }, []);

  useEffect(() => {
    if (!schemaInput?.length) {
      setParsedSchema(null);
      setErrors('');
      setSelectedWorkflow('');
      setSelectedWorkflowVariant('');
      setFormValues({});
      return;
    }
    try {
      const parsedCode = JSON.parse(schemaInput);
      setParsedSchema(parsedCode);
      setErrors('');
      // ...do something with it and return a React element
    } catch (err: any) {
      console.log(err.message);
      setErrors(err.message);
    }
  }, [schemaInput]);

  useEffect(() => {
    if (selectedWorkflow && selectedWorkflowVariant) {
      const workflow = workflows[selectedWorkflow];
      const schema = workflow[selectedWorkflowVariant];
      setSchemaInput(JSON.stringify(schema, null, 2));
    }
  }, [selectedWorkflowVariant]);

  const handleWorkflowChange = (e: SelectChangeEvent) => {
    setSelectedWorkflow(e.target.value as string);
    setSelectedWorkflowVariant('');
  };

  const handleWorkflowVariantChange = (e: SelectChangeEvent) => {
    setSelectedWorkflowVariant(e.target.value as string);
  };

  return (
    <Container>
      <Grid item container justifyContent="space-between" xs={12} sx={{ mt: 6 }}>
        <div>
          <Typography variant="h1" sx={{ pb: 0 }}>
            Schema Lab
          </Typography>
          <Typography variant="subtitle2" sx={{ pt: 1, pb: 2, width: '80%' }}>
            Paste your JSON Schema on the left, validate it, and see the generated form on the right. Use workflow profiles and Form Values to prefill
            and track updates in real-time. To understand how this works, see{' '}
            <Link
              href="https://tmtinsights.atlassian.net/wiki/spaces/TMT/pages/766083079/Polaris+JSON+Schema#%F0%9F%8E%A0-Using-the-Schema-Lab-Application"
              target="_blank"
            >
              Polaris JSON Schema Documentation.
            </Link>
          </Typography>
        </div>
        <div>
          <Grid container alignItems="center">
            <FormControl sx={{ mr: 1, width: 300 }} variant="filled">
              <InputLabel id="schema-lab-wf-label">Workflow</InputLabel>
              <Select labelId="schema-lab-wf-label" id="schema-lab-wf" value={selectedWorkflow} label="Workflow" onChange={handleWorkflowChange}>
                <MenuItem value="">none</MenuItem>
                {Object.keys(workflows)?.map((key) => (
                  <MenuItem key={key} value={key}>
                    {workflows[key].name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ width: 150 }} variant="filled">
              <InputLabel id="schema-lab-wf-label">Schema Type</InputLabel>
              <Select
                disabled={!selectedWorkflow}
                labelId="schema-lab-wf-label"
                id="schema-lab-wf"
                value={selectedWorkflowVariant}
                label="Schema Type"
                onChange={handleWorkflowVariantChange}
              >
                {['instance_schema', 'title_schema', 'template_schema', 'order_spec_schama', 'order_profile_schema'].map((type) => {
                  const typeProps = workflows[selectedWorkflow]?.[type]?.properties;
                  return (
                    <MenuItem key={type} value={type} disabled={typeProps && !Object.keys(typeProps).length}>
                      {type}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </div>
      </Grid>
      <Grid container sx={{ mt: 2 }} spacing={5}>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle1">JSON Schema:</Typography>
          <InputTextArea name="schema" id="schema" value={schemaInput} onChange={(e) => setSchemaInput(e.target.value)} rows={50} />
          <Grid item xs={12} sx={{ color: 'error.main' }}>
            <code>{errors}</code>
          </Grid>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Typography variant="subtitle1">Form Values:</Typography>
            <InputTextArea
              value={JSON.stringify(formValues, null, 2)}
              onChange={(e) => setFormValues(JSON.parse(e.target.value))}
              style={{ width: '100%', height: '200px' }}
            />
          </Grid>
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => schemaRef.current?.submit()}>
            Validate
          </Button>
        </Grid>

        <Grid item container xs={12} md={7} flexDirection="column">
          <Grid item>
            <Typography variant="subtitle1">Output:</Typography>
            <Paper sx={{ p: 3 }}>
              {parsedSchema ? (
                <SchemaForm formRef={schemaRef} schema={parsedSchema} formData={formValues} onChange={(form) => setFormValues(form.formData)} />
              ) : (
                'No schema to display'
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SchemaLab;

const InputTextArea = styled.textarea`
  background: ${({ theme }) => theme.palette.background.paper};
  color: ${({ theme }) => theme.palette.text.primary};
  width: 100%;
`;
